import { useState } from "react";
import { MapPin, ShieldCheck, ShieldAlert, Crosshair } from "lucide-react";
import { Panel } from "@/components/admin/ui";
import { OrgLocationMap } from "@/components/org/settings/OrgLocationMap";

const StatusBadge = ({ address }) => {
  if (!address.verified) {
    return <span data-testid="location-preview-status" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"><ShieldAlert size={13} /> Not Verified</span>;
  }
  if (address.location_source === "manual") {
    return <span data-testid="location-preview-status" className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 dark:bg-blue-500/10 dark:text-blue-400"><Crosshair size={13} /> Map Identified</span>;
  }
  return <span data-testid="location-preview-status" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"><ShieldCheck size={13} /> Verified</span>;
};

export const LocationPreview = ({
  address,
  title = "Location Preview",
  subtitle = "Uses your verified coordinates - never re-geocoded on page load.",
  emptyMessage = "Verify the address, or use Identify on Map, to set a location.",
  compact = false,
  fill = false,
  action,
  editable = false,
  onIdentifyConfirm,
  testId = "org-settings-location-preview",
}) => {
  const [identifying, setIdentifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasCoords = address.lat != null && address.lng != null;
  const isVerified = Boolean(address.verified) && hasCoords;

  const handleConfirm = async (lat, lng) => {
    if (!onIdentifyConfirm) return;
    setSaving(true);
    try {
      await onIdentifyConfirm(lat, lng);
      setIdentifying(false);
    } finally {
      setSaving(false);
    }
  };

  const headerAction = (
    <div className="flex items-center gap-2">
      {action}
      {editable && !identifying && (
        <button
          type="button"
          onClick={() => setIdentifying(true)}
          data-testid={`${testId}-identify-btn`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <Crosshair size={13} /> Identify on Map
        </button>
      )}
    </div>
  );

  return (
    <Panel title={title} subtitle={subtitle} action={headerAction} testId={testId} className={fill ? "flex h-full flex-col" : ""}>
      {!identifying && <div className="mb-3"><StatusBadge address={address} /></div>}
      <div className={fill ? "relative min-h-0 flex-1 overflow-hidden rounded-xl" : `relative ${compact ? "h-[160px]" : "h-[280px]"} overflow-hidden rounded-xl`}>
        {identifying ? (
          <OrgLocationMap
            lat={address.lat}
            lng={address.lng}
            mode="identify"
            onConfirm={handleConfirm}
            onCancel={() => setIdentifying(false)}
            testId={`${testId}-map`}
          />
        ) : isVerified ? (
          <OrgLocationMap lat={address.lat} lng={address.lng} mode="view" testId={`${testId}-map`} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 text-center dark:border-white/10 dark:bg-white/[0.03]" data-testid="org-settings-map-empty">
            <MapPin size={22} className="text-slate-300 dark:text-slate-600" />
            <p className="max-w-xs text-xs font-semibold text-slate-500 dark:text-slate-400">{emptyMessage}</p>
          </div>
        )}
        {saving && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70 text-xs font-bold text-slate-600 dark:bg-black/50 dark:text-slate-200">Saving location…</div>
        )}
      </div>
    </Panel>
  );
};
